import { useMemo, useState } from "react";
import { useSearch } from "@tanstack/react-router";
import {
  ArrowUpRight,
  CheckCircle2,
  ExternalLink,
  Hourglass,
  PlusCircle,
  XCircle,
} from "lucide-react";

import {
  ActiveFilterPill,
  CheckboxList,
  FilterBar as DsFilterBar,
  FilterChip,
  FilterClearAll,
} from "@/components/cinch/FilterBar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  usePipelineMovement,
  useSalesReps,
  type OpenDeal,
  type PipelineMovementBucket,
} from "../api";
import {
  ALL_PIPELINES,
  SALES_SQO_PLUS_STAGES,
  hubspotDealUrl,
} from "../constants";
import { CroForecastFilterBar } from "../components/FilterBar";
import { LockedBanner } from "../components/LockedBanner";
import { CroForecastSubNav } from "../components/SubNav";
import { fmtCents, fmtDate } from "../format";
import { resolveSearch, type CroForecastSearch } from "../search";

const BUCKET_META: Record<
  PipelineMovementBucket["bucket"],
  { label: string; icon: React.ReactNode; tone: string }
> = {
  closed_won: {
    label: "Closed-won this week",
    icon: <CheckCircle2 className="h-4 w-4" />,
    tone: "text-success",
  },
  advanced: {
    label: "Advanced this week",
    icon: <ArrowUpRight className="h-4 w-4" />,
    tone: "text-power",
  },
  new_pipeline: {
    label: "New pipeline (entered SQ)",
    icon: <PlusCircle className="h-4 w-4" />,
    tone: "text-info",
  },
  lost: {
    label: "Lost / no-decision",
    icon: <XCircle className="h-4 w-4" />,
    tone: "text-danger",
  },
  pushed: {
    label: "Pushed beyond month",
    icon: <Hourglass className="h-4 w-4" />,
    tone: "text-warning",
  },
};

export function CroForecastPipelineMovementPage() {
  const search = useSearch({
    from: "/cro-forecast/pipeline-movement",
  }) as CroForecastSearch;
  const { year, week } = resolveSearch(search);

  // All three filters are multi-select. Local state — URL already
  // carries year+week; filters are tactical and reset between sessions.
  const [pipelines, setPipelines] = useState<string[]>([]);
  const [stages, setStages] = useState<string[]>([]);
  const [reps, setReps] = useState<string[]>([]);

  const filters = useMemo(
    () => ({ pipelines, stages, reps }),
    [pipelines, stages, reps],
  );
  const { data, isLoading, error } = usePipelineMovement(year, week, filters);
  const repsList = useSalesReps();

  const hasAnyFilter =
    pipelines.length > 0 || stages.length > 0 || reps.length > 0;
  const clearAll = () => {
    setPipelines([]);
    setStages([]);
    setReps([]);
  };

  // Map HubSpot owner id → display name for the active-filter pills.
  const repNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of repsList.data ?? []) m.set(r.hubspot_owner_id, r.display_name);
    return m;
  }, [repsList.data]);

  return (
    <div className="flex flex-col">
      <CroForecastFilterBar search={search} />
      <CroForecastSubNav search={search} />
      <div className="flex flex-col gap-4 p-4 md:p-6">
        <LockedBanner
          status={data?.lock_status}
          isoYear={year}
          isoWeek={week}
        />

        {/* Pipeline / Stage / Rep filter chips (DS FilterBar). All three
            are multi-select — pass empty arrays to clear. Filters apply
            on the live path only; past-locked snapshots are frozen. */}
        <DsFilterBar>
          <FilterChip
            label="Pipeline"
            count={pipelines.length}
            valueLabel={
              pipelines.length === 1
                ? pipelines[0]
                : pipelines.length > 1
                ? `${pipelines.length} pipelines`
                : undefined
            }
          >
            <CheckboxList
              options={ALL_PIPELINES.map((p) => ({ value: p, label: p }))}
              selected={pipelines}
              onChange={setPipelines}
            />
          </FilterChip>

          <FilterChip
            label="Stage"
            count={stages.length}
            valueLabel={
              stages.length === 1
                ? stages[0]
                : stages.length > 1
                ? `${stages.length} stages`
                : undefined
            }
          >
            <CheckboxList
              options={SALES_SQO_PLUS_STAGES.map((s) => ({ value: s, label: s }))}
              selected={stages}
              onChange={setStages}
            />
          </FilterChip>

          <FilterChip
            label="Rep"
            count={reps.length}
            valueLabel={
              reps.length === 1
                ? repNameById.get(reps[0]!) ?? reps[0]
                : reps.length > 1
                ? `${reps.length} reps`
                : undefined
            }
          >
            <CheckboxList
              options={(repsList.data ?? []).map((r) => ({
                value: r.hubspot_owner_id,
                label: r.display_name,
              }))}
              selected={reps}
              onChange={setReps}
            />
          </FilterChip>

          {pipelines.map((p) => (
            <ActiveFilterPill
              key={`pl-${p}`}
              label={p}
              onRemove={() => setPipelines(pipelines.filter((x) => x !== p))}
            />
          ))}
          {stages.map((s) => (
            <ActiveFilterPill
              key={`st-${s}`}
              label={s}
              onRemove={() => setStages(stages.filter((x) => x !== s))}
            />
          ))}
          {reps.map((r) => (
            <ActiveFilterPill
              key={`rp-${r}`}
              label={repNameById.get(r) ?? r}
              onRemove={() => setReps(reps.filter((x) => x !== r))}
            />
          ))}

          {hasAnyFilter && <FilterClearAll onClick={clearAll} />}
        </DsFilterBar>

        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {error && (
          <p className="text-sm text-destructive">
            Failed: {(error as Error).message}
          </p>
        )}
        {data && (
          <>
            <p className="text-sm text-muted-foreground">
              SQO-and-beyond deal motion for ISO Wk {data.iso_week} (
              {data.week_start} → {data.week_end_exclusive}). Click the open-in-HubSpot
              icon on any row to jump straight to the deal.
            </p>
            {data.buckets.map((b) => (
              <BucketCard key={b.bucket} bucket={b} />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function BucketCard({ bucket }: { bucket: PipelineMovementBucket }) {
  const meta = BUCKET_META[bucket.bucket];
  const isPushed = bucket.bucket === "pushed";
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between gap-3 text-sm">
          <span className={`flex items-center gap-2 ${meta.tone}`}>
            {meta.icon}
            <span>{meta.label}</span>
          </span>
          <span className="font-mono text-xs text-muted-foreground">
            {bucket.deal_count} deals · {fmtCents(bucket.total_amount_cents)}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {bucket.deals.length === 0 ? (
          <p className="text-sm text-muted-foreground">No deals in this bucket.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Deal</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                {isPushed ? (
                  <>
                    <TableHead>Pushed-to close date</TableHead>
                    <TableHead>Moved at</TableHead>
                  </>
                ) : (
                  <TableHead>Date</TableHead>
                )}
                <TableHead className="w-[1%] text-right">HubSpot</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bucket.deals.slice(0, 50).map((d) => (
                <DealRow key={d.hubspot_deal_id} d={d} isPushed={isPushed} />
              ))}
              {bucket.deals.length > 50 && (
                <TableRow>
                  <TableCell
                    colSpan={isPushed ? 7 : 6}
                    className="text-center text-xs text-muted-foreground"
                  >
                    + {bucket.deals.length - 50} more (showing first 50)
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function DealRow({ d, isPushed }: { d: OpenDeal; isPushed: boolean }) {
  return (
    <TableRow>
      <TableCell className="font-medium">{d.name}</TableCell>
      <TableCell className="text-xs text-muted-foreground">{d.stage_name}</TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {d.owner_display_name ?? "—"}
      </TableCell>
      <TableCell className="text-right font-mono">
        {d.amount_cents != null ? fmtCents(d.amount_cents) : "—"}
      </TableCell>
      {isPushed ? (
        <>
          <TableCell className="font-mono text-xs text-foreground">
            {fmtDate(d.close_date ?? null)}
          </TableCell>
          <TableCell className="font-mono text-xs text-muted-foreground">
            {/* moved_at carries the deal.modified_at timestamp — last time the
                AE touched the record, which is the best proxy we have for
                "when did they push this." */}
            {d.moved_at ? fmtDate(d.moved_at.slice(0, 10)) : "—"}
          </TableCell>
        </>
      ) : (
        <TableCell className="font-mono text-xs text-muted-foreground">
          {fmtDate(d.close_date ?? null)}
        </TableCell>
      )}
      <TableCell className="text-right">
        <Button
          size="icon"
          variant="ghost"
          asChild
          title="Open in HubSpot"
          className="h-7 w-7"
        >
          <a
            href={hubspotDealUrl(d.hubspot_deal_id)}
            target="_blank"
            rel="noreferrer noopener"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </Button>
      </TableCell>
    </TableRow>
  );
}
